export function resolveSeedePrompt(content) {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    // Convert array of content objects to a string
    return content
      .map((item) => {
        if (item.type === 'text') {
          return item.text;
        } else if (item.type === 'image_url' && item.image_url && item.image_url.url) {
          const url = item.image_url.url.trim();
          return `[SeedeImage##${url}]`;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

export function resolveSeedeReferenceImage(images) {
  if (!images || !images.length) return '';
  const imageContent = images
    .map((image) => {
      const url = typeof image === 'string' ? image : image.url;
      const tag = typeof image === 'object' && image.tag ? image.tag : '';

      // Escape single quotes in url and tag
      const escapedUrl = url.replace(/'/g, "\\'");
      const escapedTag = tag.replace(/'/g, "\\'");

      return `@SeedeReferenceImage(url: '${escapedUrl}', tag: '${escapedTag}')`;
    })
    .join('\n');

  return `
${imageContent}
  `;
}

export function parseSeedeReferenceImage(content) {
  if (!content) return { content: '', images: [] };

  // Extract reference images from the content
  const imageRegex = /@SeedeReferenceImage\(url:\s*'([^']*)',\s*tag:\s*'([^']*)'\)/g;
  const images = [];
  let match;

  while ((match = imageRegex.exec(content)) !== null) {
    const url = match[1].replace(/\\'/g, "'"); // Unescape single quotes
    const tag = match[2].replace(/\\'/g, "'"); // Unescape single quotes
    images.push({
      url,
      tag: tag || undefined,
    });
  }

  // Remove the reference image tags to get clean text
  const text = content.replace(imageRegex, '');

  if (!images.length) {
    return parseSeedeReferenceImageLegacy(text);
  }

  return {
    content: text,
    images,
  };
}

export function parseSeedeReferenceImageLegacy(content) {
  if (!content) return { content: '', images: [] };

  // Extract image URLs from the content
  const imageRegex = /\[SeedeImage##([^\]]+)\]/g;
  const images = [];
  let match;

  while ((match = imageRegex.exec(content)) !== null) {
    images.push({
      url: match[1],
      tag: '',
    });
  }

  // Remove the image tags to get clean text
  const text = content.replace(imageRegex, '');

  return {
    content: text,
    images,
  };
}

export function parseSeedeTheme(content) {
  if (!content) return { content: '', theme: null };

  // Extract theme data from the content
  const themeRegex = /@SeedeTheme\(([^)]+)\)/g;
  let theme = null;
  let match;

  while ((match = themeRegex.exec(content)) !== null) {
    try {
      const themeData = JSON.parse(match[1]);
      theme = {
        value: themeData.value,
        label: themeData.label,
        description: themeData.description,
        colors: themeData.colors,
        primaryColor: themeData.primaryColor,
        secondaryColor: themeData.secondaryColor,
      };
      // Only use the first theme found
      break;
    } catch (error) {
      console.error('Failed to parse theme tag:', error);
    }
  }

  // Remove the theme tags to get clean text
  const text = content.replace(themeRegex, '');

  return {
    content: text,
    theme,
  };
}

export function parseSeedeDocument(content) {
  if (!content) return { content: '', documents: [] };

  // Extract document tags from the content
  // Use a more robust regex that handles nested parentheses in JSON content
  const documentRegex = /@SeedeDocument\((\{.*?\})\)/g;
  const documents = [];
  let documentMatch;

  while ((documentMatch = documentRegex.exec(content)) !== null) {
    try {
      // Handle potential nested parentheses by finding matching braces
      let jsonStr = documentMatch[1];
      let braceCount = 0;
      let endIndex = 0;

      for (let i = 0; i < jsonStr.length; i++) {
        if (jsonStr[i] === '{') braceCount++;
        if (jsonStr[i] === '}') braceCount--;
        if (braceCount === 0) {
          endIndex = i + 1;
          break;
        }
      }

      // If we have unmatched braces, we need to find the complete JSON
      if (braceCount !== 0) {
        // Look for the complete JSON string by finding the closing brace
        const fullMatch = content.slice(documentMatch.index);
        const startPos = fullMatch.indexOf('{');
        let braces = 0;
        let endPos = -1;

        for (let i = startPos; i < fullMatch.length; i++) {
          if (fullMatch[i] === '{') braces++;
          if (fullMatch[i] === '}') braces--;
          if (braces === 0) {
            endPos = i + 1;
            break;
          }
        }

        if (endPos > 0) {
          jsonStr = fullMatch.slice(startPos, endPos);
        }
      } else {
        jsonStr = jsonStr.slice(0, endIndex);
      }

      const documentData = JSON.parse(jsonStr);
      documents.push({
        filename: documentData.filename || documentData.name,
        id: documentData.id,
      });
    } catch (error) {
      console.error('Failed to parse document tag:', error);
    }
  }

  // Remove the document tags to get clean text
  const text = content.replace(/@SeedeDocument\(\{.*?\}\)/g, '');

  return {
    content: text,
    documents,
  };
}

export function parseSeedeMaterial(content) {
  if (!content) return { content: '', materials: [] };

  // Extract material tags from the content
  // Use a more robust regex that handles nested parentheses in JSON content
  const materialRegex = /@SeedeMaterial\((\{.*?\})\)/g;
  const materials = [];
  let materialMatch;

  while ((materialMatch = materialRegex.exec(content)) !== null) {
    try {
      // Handle potential nested parentheses by finding matching braces
      let jsonStr = materialMatch[1];
      let braceCount = 0;
      let endIndex = 0;

      for (let i = 0; i < jsonStr.length; i++) {
        if (jsonStr[i] === '{') braceCount++;
        if (jsonStr[i] === '}') braceCount--;
        if (braceCount === 0) {
          endIndex = i + 1;
          break;
        }
      }

      // If we have unmatched braces, we need to find the complete JSON
      if (braceCount !== 0) {
        // Look for the complete JSON string by finding the closing brace
        const fullMatch = content.slice(materialMatch.index);
        const startPos = fullMatch.indexOf('{');
        let braces = 0;
        let endPos = -1;

        for (let i = startPos; i < fullMatch.length; i++) {
          if (fullMatch[i] === '{') braces++;
          if (fullMatch[i] === '}') braces--;
          if (braces === 0) {
            endPos = i + 1;
            break;
          }
        }

        if (endPos > 0) {
          jsonStr = fullMatch.slice(startPos, endPos);
        }
      } else {
        jsonStr = jsonStr.slice(0, endIndex);
      }

      const materialData = JSON.parse(jsonStr);
      materials.push({
        filename: materialData.filename,
        url: materialData.url,
        width: materialData.width,
        height: materialData.height,
        aspectRatio: materialData.aspectRatio,
        tag: materialData.tag,
      });
    } catch (error) {
      console.error('Failed to parse material tag:', error);
    }
  }

  // Remove the material tags to get clean text
  const text = content.replace(/@SeedeMaterial\(\{.*?\}\)/g, '');

  return {
    content: text,
    materials,
  };
}

export function parseSeedePrompt(content) {
  if (!content) return { text: '', images: [], materials: [], theme: null, documents: [] };

  const themePayload = parseSeedeTheme(content);
  content = themePayload.content;

  const documentPayload = parseSeedeDocument(content);
  content = documentPayload.content;

  const materialPayload = parseSeedeMaterial(content);
  content = materialPayload.content;

  const stylePayload = parseSeedeStyle(content);
  content = stylePayload.content;

  const referenceImagePayload = parseSeedeReferenceImage(content);

  // Remove the image tags to get clean text
  const text = referenceImagePayload.content;

  return {
    text,
    images: referenceImagePayload.images,
    materials: materialPayload.materials,
    theme: themePayload.theme,
    style: stylePayload.style,
    documents: documentPayload.documents.map((asset) => {
      asset.parsed = true;
      return asset;
    }),
  };
}

export function resolveAssetsContent(assets) {
  if (!assets || !assets.length) return '';
  const assetsContent = assets
    .map((asset) => {
      const w = asset.meta?.w || asset.width || 128;
      const h = asset.meta?.h || asset.height || 128;
      return `
  @SeedeMaterial(${JSON.stringify({
    filename: asset.name,
    url: asset.url,
    width: w,
    height: h,
    aspectRatio: Math.round((100 * w) / h) / 100,
    tag: asset.tag || '',
  })})`;
    })
    .join('\n');

  return `
  ${assetsContent}
    `;
}

export function resolveDocumentContent(documents) {
  if (!documents || !documents.length) return '';
  const documentContent = documents
    .map((asset) => {
      return `
  @SeedeDocument(${JSON.stringify({
    filename: asset.filename || asset.name,
    id: asset.id,
  })})`;
    })
    .join('\n');

  return `
  ${documentContent}
    `;
}

export function resolveSeedeStyle(value, label) {
  const escapedValue = value.replace(/'/g, "\\'");
  const escapedLabel = label.replace(/'/g, "\\'");
  return `SeedeStyle('${escapedValue}', '${escapedLabel}')`;
}

export function parseSeedeStyle(content) {
  if (!content) return { content: '', style: null };

  // Extract style data from the content
  const styleRegex = /SeedeStyle\('([^']+)',\s*'([^']+)'\)/g;
  let style = null;
  let match;

  while ((match = styleRegex.exec(content)) !== null) {
    const value = match[1].replace(/\\'/g, "'");
    const label = match[2].replace(/\\'/g, "'");
    style = {
      value,
      label,
    };
    // Only use the first style found
    break;
  }

  // Remove the style tags to get clean text
  const text = content.replace(styleRegex, '');

  return {
    content: text,
    style,
  };
}
